import React, { useState, useEffect, useContext } from "react";
import { Box, Button, Grid, Text, HStack, useToast } from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

const COMPANY_CODES = [
  "EBL", "EMX", "ENL", "ELPL", "ENZ", "EPC", "ESA", "EPE", "EPP", "EWPL", "EAU", "EPL", "EMN", "EPDR", "EUK", "EBT", "EMCT", "EUS", "AVL", "LPK", "MNPI", "MPI", "TII", "TIL", "TIM", "ZHL"
];
const companyOptions = COMPANY_CODES.map(code => ({ label: code, value: code }));

const SaaDetailsTab = () => {
  const toast = useToast();
  const { workflowProcess } = useContext(AppContext);

  const [profitCenterOptions, setProfitCenterOptions] = useState<any[]>([]);
  const [glAccountsOptions, setGlAccountsOptions] = useState<any[]>([]);
  const [plantCodeOptions, setPlantCodeOptions] = useState<any[]>([]);
  const [businessPlaceOptions, setBusinessPlaceOptions] = useState<any[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<any[]>([]);
  const [businessAreaOptions, setBusinessAreaOptions] = useState<any[]>([]);
  const [internalOrderOptions, setInternalOrderOptions] = useState<any[]>([]);

  const [form, setForm] = useState({
    companyCode: null as any,
    profitCenter: null as any,
    costCenter: null as any,
    glAccount: null as any,
    internalOrder: null as any,
    plantCode: null as any,
    businessPlace: null as any,
    businessArea: null as any,
  });

  const updateForm = (updates: any) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  // Base fetches (depend only on company code)
  useEffect(() => {
    if (!form.companyCode?.value) {
      setProfitCenterOptions([]);
      setGlAccountsOptions([]);
      setPlantCodeOptions([]);
      setBusinessPlaceOptions([]);
      setCostCenterOptions([]);
      setBusinessAreaOptions([]);
      setInternalOrderOptions([]);
      return;
    }

    const fetchDropdowns = async () => {
      const companyCode = form.companyCode.value;

      const fetchType = async (type: string, setter: any, mapper: (data: any) => any) => {
        try {
          const payload = { type, companyCode, ...workflowProcess };
          const res: any = await appRequest("user", "masterFinancialApi", payload);
          if (res?.status === "success" && Array.isArray(res?.data)) {
            setter(res.data.map(mapper));
          } else {
            setter([]);
          }
        } catch (error) {
          console.error(`Failed to load ${type}`, error);
          setter([]);
        }
      };

      const stringMapper = (item: any) => {
        if (typeof item === 'string') return { label: item, value: item };
        return { label: item.label || item.value || JSON.stringify(item), value: item.value || item.id || item };
      };

      await Promise.all([
        fetchType("getProfitCenter", setProfitCenterOptions, (pc: any) => ({
          label: pc.label || pc.profitCenter,
          value: pc.profitCenter,
        })),
        fetchType("getGlAccount", setGlAccountsOptions, (item: any) => ({
          label: item.label || `${item.glAccount} - ${item.description}`,
          value: item.glAccount,
        })),
        fetchType("getPlantCode", setPlantCodeOptions, stringMapper),
      ]);
    };

    fetchDropdowns();
  }, [form.companyCode, workflowProcess]);

  // Fetch Business Place based on Plant Code
  useEffect(() => {
    if (!form.companyCode?.value || !form.plantCode?.value) {
      setBusinessPlaceOptions([]);
      return;
    }

    const fetchBusinessPlace = async () => {
      try {
        const payload = {
          type: "getBusinessPlace",
          companyCode: form.companyCode.value,
          plantCode: form.plantCode.value,
          ...workflowProcess,
        };
        const res: any = await appRequest("user", "masterFinancialApi", payload);
        if (res?.status === "success" && Array.isArray(res?.data)) {
          setBusinessPlaceOptions(res.data.map((bp: any) => {
            if (typeof bp === 'string') return { label: bp, value: bp };
            return { label: bp.label || bp.value, value: bp.value };
          }));
        } else {
            setBusinessPlaceOptions([]);
        }
      } catch (error) {
        console.error("Failed to load Business Place", error);
        setBusinessPlaceOptions([]);
      }
    };

    fetchBusinessPlace();
  }, [form.plantCode, form.companyCode, workflowProcess]);

  // Fetch Cost Center based on Profit Center
  useEffect(() => {
    if (!form.companyCode?.value || !form.profitCenter?.value) {
      setCostCenterOptions([]);
      return;
    }

    const fetchCostCenter = async () => {
      try {
        const payload = {
          type: "getCostCenter",
          companyCode: form.companyCode.value,
          profitCenter: form.profitCenter.value,
          ...workflowProcess,
        };
        const res: any = await appRequest("user", "masterFinancialApi", payload);
        if (res?.status === "success" && Array.isArray(res?.data)) {
          setCostCenterOptions(res.data.map((cc: any) => ({
            label: cc.label || cc.costCenter,
            value: cc.costCenter,
          })));
        } else {
            setCostCenterOptions([]);
        }
      } catch (error) {
        console.error("Failed to load Cost Center", error);
        setCostCenterOptions([]);
      }
    };

    fetchCostCenter();
  }, [form.profitCenter, form.companyCode, workflowProcess]);

  // Fetch Business Area based on Cost Center
  useEffect(() => {
    if (!form.companyCode?.value || !form.profitCenter?.value || !form.costCenter?.value) {
      setBusinessAreaOptions([]);
      return;
    }

    const fetchBusinessArea = async () => {
      try {
        const payload = {
          type: "getBusinessArea",
          companyCode: form.companyCode.value,
          profitCenter: form.profitCenter.value,
          costCenter: form.costCenter.value,
          ...workflowProcess,
        };
        const res: any = await appRequest("user", "masterFinancialApi", payload);
        if (res?.status === "success" && Array.isArray(res?.data)) {
          setBusinessAreaOptions(res.data.map((ba: any) => {
            if (typeof ba === 'string') return { label: ba, value: ba };
            return { label: ba.label || ba.value, value: ba.value };
          }));
        } else {
            setBusinessAreaOptions([]);
        }
      } catch (error) {
        console.error("Failed to load Business Area", error);
        setBusinessAreaOptions([]);
      }
    };

    fetchBusinessArea();
  }, [form.costCenter, form.profitCenter, form.companyCode, workflowProcess]);

  // Fetch Internal Order based on GL Account
  useEffect(() => {
    if (!form.companyCode?.value || !form.glAccount?.value) {
      setInternalOrderOptions([]);
      return;
    }

    const fetchInternalOrder = async () => {
      try {
        const payload: any = {
          type: "getInternalOrder",
          companyCode: form.companyCode.value,
          glAccount: form.glAccount.value,
          ...workflowProcess,
        };

        if (form.profitCenter?.value) {
          payload.profitCenter = form.profitCenter.value;
        }

        const res: any = await appRequest("user", "masterFinancialApi", payload);
        if (res?.status === "success" && Array.isArray(res?.data)) {
          setInternalOrderOptions(res.data.map((io: any) => {
            if (typeof io === 'string') return { label: io, value: io };
            return { label: io.label || io.value, value: io.value };
          }));
        } else {
            setInternalOrderOptions([]);
        }
      } catch (error) {
        console.error("Failed to load Internal Order", error);
        setInternalOrderOptions([]);
      }
    };

    fetchInternalOrder();
  }, [form.glAccount, form.profitCenter, form.companyCode, workflowProcess]);

  const handleReset = () => {
    setForm({
      companyCode: null as any,
      profitCenter: null as any,
      costCenter: null as any,
      glAccount: null as any,
      internalOrder: null as any,
      plantCode: null as any,
      businessPlace: null as any,
      businessArea: null as any,
    });
    setProfitCenterOptions([]);
    setGlAccountsOptions([]);
    setPlantCodeOptions([]);
    setBusinessPlaceOptions([]);
    setCostCenterOptions([]);
    setBusinessAreaOptions([]);
    setInternalOrderOptions([]);
  };

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button 
          colorScheme="red" 
          variant="outline"
          size="sm" 
          px={6} 
          onClick={handleReset} 
          shadow="sm" 
          _hover={{ bg: "red.50", transform: "translateY(-1px)", shadow: "md" }} 
          transition="all 0.2s"
          borderRadius="md"
          fontWeight="bold"
        >
          Reset Details
        </Button>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" mb={6} borderColor="gray.200">
        <HStack mb={8} borderBottom="3px solid" borderColor="blue.500" pb={2} width="fit-content">
          <Text fontSize="2xl" fontWeight="extrabold" color="gray.800">
            Financial Details
          </Text>
        </HStack>

        {/* Organization Context Section */}
        <Box bg="blue.50" p={6} borderRadius="xl" mb={8} border="1px solid" borderColor="blue.100">
          <Text fontSize="sm" fontWeight="bold" color="blue.600" textTransform="uppercase" mb={4} letterSpacing="widest">
            Organization Context
          </Text>
          <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
            <CustomInput
              name="companyCode"
              label="Company Code"
              type="select"
              isBoxSelect
              options={companyOptions}
              value={form.companyCode}
              onChange={(val: any) => {
                updateForm({
                  companyCode: val,
                  profitCenter: null,
                  glAccount: null,
                  plantCode: null,
                  businessPlace: null,
                  costCenter: null,
                  businessArea: null,
                  internalOrder: null,
                });
              }}
            />
          </Grid>
        </Box>

        <Grid gridTemplateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
          {/* Cost & Profit Centers Section */}
          <Box bg="white" p={6} borderRadius="xl" shadow="sm" border="1px solid" borderColor="gray.200" _hover={{ shadow: "md", borderColor: "blue.200", transition: "all 0.3s" }}>
            <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={5} letterSpacing="widest" borderBottom="1px solid" borderColor="gray.100" pb={2}>
              Cost & Profit Structure
            </Text>
            <HStack spacing={6} align="flex-start" flexDirection="column" w="100%">
              <Box w="100%">
                <CustomInput
                  name="profitCenter"
                  label="Profit Center"
                  type="select"
                  isBoxSelect
                  options={profitCenterOptions}
                  value={form.profitCenter}
                  onChange={(val: any) => updateForm({ profitCenter: val, costCenter: null, businessArea: null, internalOrder: null })}
                />
              </Box>
              <Box w="100%">
                <CustomInput
                  name="costCenter"
                  label="Cost Center"
                  type="select"
                  isBoxSelect
                  options={costCenterOptions}
                  value={form.costCenter}
                  onChange={(val: any) => updateForm({ costCenter: val, businessArea: null })}
                />
              </Box>
              <Box w="100%">
                <CustomInput
                  name="businessArea"
                  label="Business Area"
                  type="select"
                  isBoxSelect
                  options={businessAreaOptions}
                  value={form.businessArea}
                  onChange={(val: any) => updateForm({ businessArea: val })}
                />
              </Box>
            </HStack>
          </Box>

          {/* Account & Plant Section */}
          <Box bg="white" p={6} borderRadius="xl" shadow="sm" border="1px solid" borderColor="gray.200" _hover={{ shadow: "md", borderColor: "blue.200", transition: "all 0.3s" }}>
            <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={5} letterSpacing="widest" borderBottom="1px solid" borderColor="gray.100" pb={2}>
              Account & Plant Info
            </Text>
            <Grid gridTemplateColumns="1fr 1fr" gap={6}>
              <Box gridColumn="span 2">
                <CustomInput
                  name="glAccount"
                  label="GL Account"
                  type="select"
                  isBoxSelect
                  options={glAccountsOptions}
                  value={form.glAccount}
                  onChange={(val: any) => updateForm({ glAccount: val, internalOrder: null })}
                />
              </Box>
              <Box gridColumn="span 2">
                <CustomInput
                  name="internalOrder"
                  label="Internal Order"
                  type="select"
                  isBoxSelect
                  options={internalOrderOptions}
                  value={form.internalOrder}
                  onChange={(val: any) => updateForm({ internalOrder: val })}
                />
              </Box>
              <Box gridColumn={{ base: "span 2", md: "span 1" }}>
                <CustomInput
                  name="plantCode"
                  label="Plant Code"
                  type="select"
                  isBoxSelect
                  options={plantCodeOptions}
                  value={form.plantCode}
                  onChange={(val: any) => updateForm({ plantCode: val, businessPlace: null })}
                />
              </Box>
              <Box gridColumn={{ base: "span 2", md: "span 1" }}>
                <CustomInput
                  name="businessPlace"
                  label="Business Place"
                  type="select"
                  isBoxSelect
                  options={businessPlaceOptions}
                  value={form.businessPlace}
                  onChange={(val: any) => updateForm({ businessPlace: val })}
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default SaaDetailsTab;
